/** @typedef {'survey' | 'video' | 'freeKick' | 'wheelOfFortune' | 'backgammon'} ActivityReviewActionKind */

/**
 * Build a reusable activity-review action config object.
 *
 * @param {string} label         Title-case label shown in modals / alerts (e.g. 'Free Kick')
 * @param {string} lowerPhrase   Lower-case phrase used in sentences (e.g. 'free kick')
 * @param {string} filePrefix    Prefix for downloaded reports (e.g. 'FreeKick_')
 * @returns {object}             Full config object for the activity kind
 */
const buildActivityReviewConfig = (label, lowerPhrase, filePrefix) => ({
  label,
  publishModalTitle: `Publish ${label}`,
  publishModalText: `This ${label} will be published immediately. Are you sure you want to continue?`,
  unpublishModalTitle: `Unpublish ${label}`,
  unpublishModalText: `This ${label} will be unpublished and will remain so until it is manually reactivated. Are you sure you want to proceed?`,
  publishedAlertTitle: `${label} Successfully Published`,
  unpublishedAlertTitle: `${label} Unpublished`,
  unpublishedAlertSubTitle: `This ${lowerPhrase} is now paused, You can reactivate it at any time.`,
  failedPublish: `Failed to publish ${lowerPhrase}`,
  failedUnpublish: `Failed to unpublish ${lowerPhrase}`,
  errorPublishing: `Error publishing ${lowerPhrase}:`,
  errorUnpublishing: `Error unpublishing ${lowerPhrase}:`,
  failedDownload: `Failed to download ${lowerPhrase}`,
  errorDownloading: `Error downloading ${lowerPhrase}:`,
  noRecordsFound: `No records found for this ${lowerPhrase}`,
  downloadFilePrefix: filePrefix
});

/** @type {Record<ActivityReviewActionKind, object>} */
export const ACTIVITY_REVIEW_ACTION_CONFIG = {
  survey: buildActivityReviewConfig('Survey', 'survey', 'Survey_'),
  video: buildActivityReviewConfig('Video', 'video', 'Video_'),
  wheelOfFortune: buildActivityReviewConfig(
    'Wheel of Fortune Game',
    'wheel of fortune game',
    'WheelOfFortune_'
  ),
  freeKick: buildActivityReviewConfig(
    'Free Kick Game',
    'free kick game',
    'FreeKick_'
  ),
  backgammon: buildActivityReviewConfig(
    'Backgammon Game',
    'backgammon game',
    'Backgammon_'
  )
};

/**
 * @param {ActivityReviewActionKind | null | undefined} activityKind
 * @returns {typeof ACTIVITY_REVIEW_ACTION_CONFIG.survey | null}
 */
export const getActivityReviewActionConfig = (activityKind) =>
  activityKind ? (ACTIVITY_REVIEW_ACTION_CONFIG[activityKind] ?? null) : null;
